import { createContext, useContext, useEffect, useState } from 'react';
import {
    onSnapshot, doc, collection,
    query,
    orderBy,
    limit,
    getDocs,
    where,
    startAfter,
} from 'firebase/firestore';
import { db } from './firebase_init';
import { useLanguage } from '../Localization/LanguageLayout';
import { useDialog } from '../Providers/Dialog/DialogProvider';

const LOAD_LIMIT = 3;
const INITIAL_LOAD_LIMIT = 3;
const order = "desc";

const BlogContext = createContext(null);

export const BlogProvider = ({ children }) => {
    const { setLoading } = useDialog();
    const [docs, setDocs] = useState([]);
    const [lastDoc, setLastDoc] = useState(null);
    const language = useLanguage();
    const [lastLanguage, setLastLanguage] = useState(null);

    useEffect(() => {
        if (lastLanguage === language) {
            return;
        }

        setLastLanguage(language);
        fetchInitial();
    }, [language]);

    async function fetchInitial(do_load = false) {
        console.log("Blog initial fetch");
        if (do_load) {
            setLoading(true);
        }

        try {
            const q = query(
                collection(db, "blog"),
                where("language", "==", language),
                orderBy("date", order),
                limit(INITIAL_LOAD_LIMIT)
            );

            const snap = await getDocs(q);

            setDocs(snap.docs);
            if (snap.docs.length < INITIAL_LOAD_LIMIT) {
                setLastDoc(null);
            } else {
                setLastDoc(snap.docs[snap.docs.length - 1] || null);
            }
        } catch (e) {
            console.error(e);
        }
        if (do_load) {
            setLoading(false);
        }
    }

    async function loadMore(do_load = false, load_limit = LOAD_LIMIT) {
        if (!lastDoc) return;

        if (do_load) {
            setLoading(true);
        }

        try {
            const q = query(
                collection(db, "blog"),
                where("language", "==", language),
                orderBy("date", order),
                startAfter(lastDoc),
                limit(load_limit)
            );

            const snap = await getDocs(q);

            setDocs((prev) => [...prev, ...snap.docs]);
            if (snap.docs.length < load_limit) {
                setLastDoc(null);
            } else {
                setLastDoc(snap?.docs?.[snap.docs.length - 1] || null);
            }
        } catch (e) {
            console.error(e);
            setLastDoc(null);
        }
        if (do_load) {
            setLoading(false);
        }
    }

    function getRandomArticles(count) {
        const articles = docs.map(d => d.data());
        const arr = [...articles];
        if (!Array.isArray(arr) || arr.length === 0) return [];

        const n = arr.length;
        const take = Math.min(count, n);
        const result = [...arr];

        // Partial Fisher-Yates shuffle
        for (let i = n - 1; i > n - 1 - take; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [result[i], result[j]] = [result[j], result[i]];
        }

        return result.slice(n - take);
    }

    const articles = docs.map(d => d.data());

    return (
        <BlogContext.Provider value={{ docs, articles, lastDoc, loadMore, getRandomArticles }}>
            {children}
        </BlogContext.Provider>
    );
};

export const useBlog = () => useContext(BlogContext);
